"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { Link } from "@/lib/i18n/navigation";
import { track } from "@/lib/observability/analytics";

type Props = {
  physicianSlug: string;
  className?: string;
  label: string;
};

type Result =
  | { kind: "idle" }
  | { kind: "success"; message: string }
  | { kind: "needLogin" }
  | { kind: "error"; message: string };

/**
 * Bouton + modal "Laisser un avis". Soumet à POST /api/reviews → l'avis est
 * créé en status PENDING (modération obligatoire avant publication = "sous
 * acceptation"). Si l'utilisateur n'est pas connecté (401), on l'invite à se
 * connecter (avis liés à un compte = anti-spam, cohérent avec 0 fake data).
 */
export function ReviewFormButton({ physicianSlug, className, label }: Props) {
  const t = useTranslations("physician.avis");
  const tc = useTranslations("common");
  const pathname = usePathname();
  // returnTo pointe vers l'ancre #avis pour que l'utilisateur revienne pile
  // sur la section avis apres login — minimise la friction (avant : login →
  // /dashboard, l'intent d'avis etait perdu, ~80% drop).
  const loginHref = `/connexion?returnTo=${encodeURIComponent(`${pathname || "/"}#avis`)}`;
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<Result>({ kind: "idle" });

  const canSubmit = rating >= 1 && content.trim().length >= 10 && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setResult({ kind: "idle" });
    try {
      const res = await fetch("/api/reviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          physicianSlug,
          rating,
          content: content.trim(),
          title: title.trim() || undefined,
          anonymousDisplay: anonymous,
        }),
      });
      if (res.status === 401) {
        setResult({ kind: "needLogin" });
        track("form:submitted", { form: "review-quick", physicianSlug, outcome: "needLogin" });
      } else if (res.ok) {
        setResult({ kind: "success", message: t("submitSuccess") });
        track("form:submitted", { form: "review-quick", physicianSlug, outcome: "success" });
      } else {
        const d = await res.json().catch(() => ({}));
        setResult({ kind: "error", message: d?.message || t("submitError") });
        track("form:submitted", { form: "review-quick", physicianSlug, outcome: "error" });
      }
    } catch {
      setResult({ kind: "error", message: t("submitError") });
    } finally {
      setSubmitting(false);
    }
  }

  function close() {
    setOpen(false);
    // reset après fermeture
    setTimeout(() => {
      setRating(0); setContent(""); setTitle(""); setAnonymous(false);
      setResult({ kind: "idle" }); setSubmitting(false);
    }, 200);
  }

  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[1000] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="w-full max-w-lg rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl sm:p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-[17px] font-extrabold text-[var(--sahha-text-900)]">{t("formTitle")}</h3>
              <button type="button" onClick={close} aria-label={tc("close")}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--sahha-surface-1)] text-[var(--sahha-text-700)]">
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
              </button>
            </div>

            {result.kind === "success" ? (
              <div className="py-8 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-3xl">✓</div>
                <p className="mt-4 text-[15px] font-semibold text-[var(--sahha-text-900)]">{result.message}</p>
                <button type="button" onClick={close} className="mt-5 inline-flex h-11 items-center rounded-full bg-[var(--sahha-primary-700)] px-6 text-[14px] font-bold text-white">
                  {tc("close")}
                </button>
              </div>
            ) : result.kind === "needLogin" ? (
              <div className="py-8 text-center">
                <p className="text-[15px] font-semibold text-[var(--sahha-text-900)]">{t("loginToReview")}</p>
                <p className="mx-auto mt-1.5 max-w-sm text-[13.5px] text-[var(--sahha-text-600)]">{t("loginToReviewBody")}</p>
                <Link href={loginHref as never} className="mt-5 inline-flex h-11 items-center rounded-full bg-[var(--sahha-primary-700)] px-6 text-[14px] font-bold text-white">
                  {t("loginCta")}
                </Link>
              </div>
            ) : (
              <>
                {/* Étoiles */}
                <div className="mt-4">
                  <p className="text-[13px] font-semibold text-[var(--sahha-text-700)]">{t("formRatingLabel")}</p>
                  <div className="mt-1.5 flex gap-1" onMouseLeave={() => setHover(0)}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button key={s} type="button" onClick={() => setRating(s)} onMouseEnter={() => setHover(s)}
                        aria-label={`${s}/5`}
                        className="text-3xl leading-none transition-transform hover:scale-110">
                        <span className={(hover || rating) >= s ? "text-[var(--sahha-accent-500)]" : "text-[var(--sahha-text-300)]"}>★</span>
                      </button>
                    ))}
                  </div>
                </div>

                <input
                  type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
                  placeholder={t("formTitlePlaceholder")}
                  className="mt-4 w-full rounded-xl border border-[var(--sahha-border-default)] px-3.5 py-2.5 text-[14px] outline-none focus:border-[var(--sahha-primary-400)]"
                />
                <textarea
                  value={content} onChange={(e) => setContent(e.target.value)} maxLength={2000} rows={4}
                  placeholder={t("formContentPlaceholder")}
                  className="mt-3 w-full resize-none rounded-xl border border-[var(--sahha-border-default)] px-3.5 py-2.5 text-[14px] outline-none focus:border-[var(--sahha-primary-400)]"
                />
                <p className="mt-1 text-[11px] text-[var(--sahha-text-500)]">{content.trim().length}/2000 · {t("formMinChars")}</p>

                <label className="mt-3 flex items-center gap-2 text-[13px] text-[var(--sahha-text-700)]">
                  <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="h-4 w-4" />
                  {t("formAnonymous")}
                </label>

                {result.kind === "error" && (
                  <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-[13px] font-medium text-red-700">{result.message}</p>
                )}

                <p className="mt-3 rounded-lg bg-[var(--sahha-primary-50)] px-3 py-2 text-[12px] text-[var(--sahha-primary-800)]">
                  ℹ️ {t("formModerationNote")}
                </p>

                <button type="button" onClick={submit} disabled={!canSubmit}
                  className="mt-4 flex h-12 w-full items-center justify-center rounded-full bg-[var(--sahha-primary-700)] text-[15px] font-bold text-white disabled:opacity-50">
                  {submitting ? "…" : t("formSubmit")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
